import { Clock9Icon, X } from 'lucide-react';
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link } from 'react-router';

import type { EmployeesAndDealSelects, GetAccounts } from './types';

import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue
} from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

interface FilterBarProps {
  activeDealsOnlySwitchIsChecked: boolean;
  data3: GetAccounts;
  dataEmployeesAndDealSelects: EmployeesAndDealSelects;
  newDealDialogTrigger: React.ReactNode;
  onDealSourceSearchChange: (value: string) => void;
  selectedDealSource: string[];
  selectedDealTeam: string[];
  selectedNameFilter: string | undefined;
  selectedStatusFilter: string | undefined;
  setActiveDealsOnlySwitchIsChecked: (checked: boolean) => void;
  setSelectedDealSource: (source: string[]) => void;
  setSelectedDealTeam: (team: string[]) => void;
  setSelectedNameFilter: (filter: string | undefined) => void;
  setSelectedStatusFilter: (filter: string | undefined) => void;
  tabValue: string;
}

export const FilterBar = ({
  activeDealsOnlySwitchIsChecked,
  data3,
  dataEmployeesAndDealSelects,
  newDealDialogTrigger,
  onDealSourceSearchChange,
  selectedDealSource,
  selectedDealTeam,
  selectedNameFilter,
  selectedStatusFilter,
  setActiveDealsOnlySwitchIsChecked,
  setSelectedDealSource,
  setSelectedDealTeam,
  setSelectedNameFilter,
  setSelectedStatusFilter,
  tabValue
}: FilterBarProps) => {
  const [localNameFilter, setLocalNameFilter] = useState(
    selectedNameFilter ?? ''
  );
  const isUserChangeRef = useRef(false);
  const previousPropRef = useRef(selectedNameFilter);

  // Debounce the name filter to throttle requests
  useEffect(() => {
    const timer = setTimeout(() => {
      isUserChangeRef.current = false;
      setSelectedNameFilter(localNameFilter || undefined);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [localNameFilter, setSelectedNameFilter]);

  // Sync local state when prop changes externally (e.g., filter reset)
  // Only sync when the prop actually changes from external source, not during user typing
  useEffect(() => {
    // Skip if this is a user-initiated change
    if (isUserChangeRef.current) {
      previousPropRef.current = selectedNameFilter;
      return;
    }

    // Only update if the prop actually changed externally
    if (previousPropRef.current !== selectedNameFilter) {
      const newValue = selectedNameFilter ?? '';
      startTransition(() => {
        setLocalNameFilter(newValue);
      });
      previousPropRef.current = selectedNameFilter;
    }
  }, [selectedNameFilter]);

  const filteredStatusSelections = useMemo(() => {
    if (tabValue === 'top_of_funnel') {
      return dataEmployeesAndDealSelects.statusSelections.filter(
        option =>
          option.value === 'ready_for_md_review' ||
          option.value === 'not_enough_information'
      );
    }
    if (tabValue === 'active') {
      return dataEmployeesAndDealSelects.statusSelections.filter(
        option => option.value === 'active'
      );
    }
    if (tabValue === 'inactive') {
      return dataEmployeesAndDealSelects.statusSelections.filter(
        option =>
          option.value === 'killed' ||
          option.value === 'closed' ||
          option.value === 'long_term'
      );
    }
    return dataEmployeesAndDealSelects.statusSelections;
  }, [dataEmployeesAndDealSelects.statusSelections, tabValue]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      isUserChangeRef.current = true;
      setLocalNameFilter(e.target.value);
    },
    []
  );

  return (
    <div className="bg-sidebar flex justify-between gap-x-2.5 overflow-y-hidden border-b px-5 py-1.5 md:pl-14">
      <div className="flex items-end gap-x-2.5">
        <div className="w-50">
          <Field className="gap-0">
            <FieldLabel
              className="h-5"
              htmlFor="name-field"
            >
              Name
            </FieldLabel>
            <Input
              className="bg-white"
              id="name-field"
              onChange={handleNameChange}
              placeholder="Enter name"
              value={localNameFilter}
            />
          </Field>
        </div>
        <div className="w-50">
          <Field className="gap-0">
            <FieldLabel
              className="h-5"
              htmlFor="team-field"
            >
              People
            </FieldLabel>
            <MultiSelect
              onValuesChange={setSelectedDealTeam}
              values={selectedDealTeam}
            >
              <MultiSelectTrigger
                className="w-full bg-white hover:bg-white"
                id="team-field"
              >
                <MultiSelectValue
                  overflowBehavior="cutoff"
                  placeholder="Select multiple options"
                />
              </MultiSelectTrigger>
              <MultiSelectContent>
                <MultiSelectGroup>
                  {dataEmployeesAndDealSelects.hrEmployees.edges.map(edge => (
                    <MultiSelectItem
                      key={edge.node.id}
                      keywords={[edge.node.name]}
                      value={edge.node.id}
                    >
                      {edge.node.name}
                    </MultiSelectItem>
                  ))}
                </MultiSelectGroup>
              </MultiSelectContent>
            </MultiSelect>
          </Field>
        </div>
        {(tabValue === 'top_of_funnel' || tabValue === 'inactive') && (
          <div className="w-50">
            <Field className="gap-0">
              <FieldLabel
                className="h-5"
                htmlFor="status-field"
              >
                Status
              </FieldLabel>
              <div className="relative w-full">
                <Select
                  onValueChange={setSelectedStatusFilter}
                  value={selectedStatusFilter}
                >
                  <SelectTrigger
                    className="w-full bg-white"
                    id="status-field"
                  >
                    <span
                      className={
                        selectedStatusFilter
                          ? 'block max-w-[calc(100%-32px)] truncate'
                          : ''
                      }
                    >
                      <SelectValue placeholder="Select an option" />
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {filteredStatusSelections.map(option => (
                      <SelectItem
                        key={option.name}
                        value={option.value}
                      >
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedStatusFilter && (
                  <button
                    className="ring-offset-background focus:ring-ring absolute top-1/2 right-8 -translate-y-1/2 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedStatusFilter('');
                    }}
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </Field>
          </div>
        )}
        <div className="w-50">
          <Field className="gap-0">
            <FieldLabel
              className="h-5"
              htmlFor="deal-source-field"
            >
              Deal Source
            </FieldLabel>
            <MultiSelect
              onValuesChange={setSelectedDealSource}
              values={selectedDealSource}
            >
              <MultiSelectTrigger
                className="w-full bg-white hover:bg-white"
                id="deal-source-field"
              >
                <MultiSelectValue
                  overflowBehavior="cutoff"
                  placeholder="Select multiple options"
                />
              </MultiSelectTrigger>
              <MultiSelectContent onSearchChange={onDealSourceSearchChange}>
                <MultiSelectGroup>
                  {data3.searchAccounts.map(option => (
                    <MultiSelectItem
                      key={option.id}
                      value={option.id}
                    >
                      {option.name}
                    </MultiSelectItem>
                  ))}
                </MultiSelectGroup>
              </MultiSelectContent>
            </MultiSelect>
          </Field>
        </div>
        <div
          className="flex h-9 items-center gap-x-3"
          style={{ display: 'none' }}
        >
          <Field
            className="gap-0"
            orientation="horizontal"
          >
            <Switch
              checked={activeDealsOnlySwitchIsChecked}
              id="active-deals-only-field"
              onCheckedChange={setActiveDealsOnlySwitchIsChecked}
            />
            <FieldLabel
              className="h-5"
              htmlFor="active-deals-only-field"
            >
              Active Deals Only
            </FieldLabel>
          </Field>
        </div>
      </div>
      <div className="flex items-end gap-x-2.5">
        <Button
          asChild
          variant="outline"
        >
          <Link to="/investments/pipeline-analytics">
            <Clock9Icon />
            Pipeline Analytics
          </Link>
        </Button>
        {newDealDialogTrigger}
      </div>
    </div>
  );
};
